import React, { useState, useEffect } from 'react';

const UpcomingEpisodes = () => {
  const [selectedEpisode, setSelectedEpisode] = useState(null);
  const [showNotifyModal, setShowNotifyModal] = useState(false);
  const [email, setEmail] = useState('');
  const [name, setName] = useState('');
  const [selectedEpisodes, setSelectedEpisodes] = useState([]);
  const [isSubmitted, setIsSubmitted] = useState(false);
  const [upcomingEpisodes, setUpcomingEpisodes] = useState([]);
  const [loading, setLoading] = useState(true);

  // Countdown timer state
  const [countdowns, setCountdowns] = useState({});

  // Fetch upcoming episodes from API
  const fetchUpcomingEpisodes = async () => {
    try {
      setLoading(true);
      const response = await fetch(`${process.env.REACT_APP_BACKEND_URL}/api/upcoming/public`);
      
      if (!response.ok) {
        throw new Error(`Failed to fetch upcoming episodes: ${response.status} ${response.statusText}`);
      }
      
      const data = await response.json();
      
      // Transform the data to match the expected structure
      const transformedEpisodes = data.items?.map(episode => ({
        id: episode.id,
        badge: 'COMING SOON',
        title: episode.title,
        guestName: episode.guest_name || episode.guestName,
        guestHint: `Guest: ${episode.guest_name || episode.guestName}`,
        quote: episode.quote,
        background: episode.thumbnail_url || episode.thumbnailUrl,
        releaseDate: new Date(Date.now() + Math.random() * 30 * 24 * 60 * 60 * 1000), // Random date within 30 days
        emoji: '🎙️',
        isAnonymous: false
      })) || [];
      
      setUpcomingEpisodes(transformedEpisodes);
    } catch (error) {
      console.error('Error fetching upcoming episodes:', error);
      // Fallback to empty array if API fails
      setUpcomingEpisodes([]);
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    fetchUpcomingEpisodes();
  }, []);

  // Calculate countdown timers
  useEffect(() => {
    if (upcomingEpisodes.length === 0) return;

    const updateCountdowns = () => {
      const newCountdowns = {};
      upcomingEpisodes.forEach(episode => {
        const now = new Date().getTime();
        const releaseTime = episode.releaseDate.getTime();
        const difference = releaseTime - now;

        if (difference > 0) {
          const days = Math.floor(difference / (1000 * 60 * 60 * 24));
          const hours = Math.floor((difference % (1000 * 60 * 60 * 24)) / (1000 * 60 * 60));
          const minutes = Math.floor((difference % (1000 * 60 * 60)) / (1000 * 60));
          newCountdowns[episode.id] = { days, hours, minutes };
        } else {
          newCountdowns[episode.id] = { days: 0, hours: 0, minutes: 0 };
        }
      });
      setCountdowns(newCountdowns);
    };

    updateCountdowns();
    const interval = setInterval(updateCountdowns, 60000); // Update every minute

    return () => clearInterval(interval);
  }, [upcomingEpisodes]);

  const handleNotifySubmit = async (e) => {
    e.preventDefault();
    if (email && name) {
      try {
        const response = await fetch(`${process.env.REACT_APP_BACKEND_URL}/api/newsletter`, {
          method: 'POST',
          headers: {
            'Content-Type': 'application/json',
          },
          body: JSON.stringify({
            name: name,
            email: email,
            source: 'upcoming'
          }),
        });
        
        setIsSubmitted(true);
        setTimeout(() => {
          setShowNotifyModal(false);
          setIsSubmitted(false);
          setEmail('');
          setName('');
          setSelectedEpisodes([]);
        }, 3000);
      } catch (error) {
        console.error('Failed to subscribe:', error);
      }
    } else {
      alert("Molimo unesite ime i email adresu.");
    }
  };

  const CloseIcon = () => (
    <svg className="w-6 h-6" fill="none" stroke="currentColor" viewBox="0 0 24 24">
      <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M6 18L18 6M6 6l12 12" />
    </svg>
  );

  const PlayIcon = () => (
    <svg className="w-8 h-8" fill="currentColor" viewBox="0 0 24 24">
      <path d="M8 5v14l11-7z"/>
    </svg>
  );

  return (
    <div className="min-h-screen bg-midnight text-warm-white font-geist">
      {/* Section Header */}
      <div className="pt-32 pb-12 px-6 lg:px-12">
        <div className="max-w-7xl mx-auto text-center">
          <h1 className="font-clash text-5xl lg:text-7xl font-bold mb-6 tracking-tight">
            Uskoro na Unframed Talks
          </h1>
          <p className="text-xl lg:text-2xl text-soft-gray max-w-3xl mx-auto leading-relaxed font-geist">
            Premijumski razgovori koji dolaze. Svaka priča pažljivo odabrana, svako otkriće vrijedno čekanja. Budite spremni za ono što slijedi.
          </p>
        </div>
      </div>

      {/* Trailer Wall - Horizontal Scroll */}
      <div className="px-6 lg:px-12 mb-20">
        <div className="max-w-7xl mx-auto">
          {loading ? (
            <div className="text-center py-12">
              <div className="text-electric-yellow text-lg">Učitava nadolazeće epizode...</div>
            </div>
          ) : upcomingEpisodes.length === 0 ? (
            <div className="text-center py-12">
              <div className="text-soft-gray text-lg">Nema nadolazećih epizoda u ovom trenutku.</div>
              <p className="text-soft-gray text-sm mt-2">Vratite se uskoro za nove epizode!</p>
            </div>
          ) : (
            <>
              {/* Desktop: Horizontal Scroll */}
              <div className="hidden lg:block">
                <div className="flex overflow-x-auto space-x-8 pb-6 scrollbar-hide">
              {upcomingEpisodes.map((episode) => (
                <div
                  key={episode.id}
                  className="flex-shrink-0 w-80 h-auto min-h-96 relative bg-charcoal/40 backdrop-blur-sm border border-slate-dark/30 rounded-3xl overflow-hidden hover:scale-[1.02] transition-all duration-700 group cursor-pointer"
                  onClick={() => setSelectedEpisode(episode)}
                >
                  {/* Background Image */}
                  <div 
                    className="absolute inset-0 bg-cover bg-center opacity-30 group-hover:opacity-50 transition-opacity duration-700"
                    style={{ backgroundImage: `url(${episode.background})` }}
                  />
                  
                  {/* Dark Overlay */}
                  <div className="absolute inset-0 bg-gradient-to-t from-midnight via-midnight/80 to-midnight/40" />

                  {/* Content */}
                  <div className="relative h-full flex flex-col justify-between p-6">
                    {/* Top Section */}
                    <div>
                      <div className="flex items-center justify-center mb-4">
                        <span className="bg-electric-yellow text-midnight px-3 py-1 rounded-full text-sm font-bold uppercase tracking-wide">
                          COMING SOON
                        </span>
                      </div>

                      <h3 className="font-clash text-2xl font-bold leading-tight mb-3 group-hover:text-electric-yellow transition-colors duration-500">
                        {episode.title}
                      </h3>

                      {/* Guest Hint */}
                      <p className="text-soft-gray mb-2 text-sm uppercase tracking-wide font-medium">
                        {episode.isAnonymous ? episode.guestHint : `Guest: ${episode.guestName || episode.guestHint}`}
                      </p>

                      {/* Quote */}
                      <blockquote className="text-electric-yellow italic text-lg leading-snug">
                        "{episode.quote}"
                      </blockquote>
                    </div>

                    {/* Bottom Section */}
                    <div>
                      {/* CTA Button */}
                      <button 
                        className="w-full bg-electric-yellow text-midnight px-4 py-3 rounded-xl font-bold hover:bg-electric-yellow/90 transition-all duration-300 flex items-center justify-center gap-2"
                        onClick={(e) => {
                          e.stopPropagation();
                          setShowNotifyModal(true);
                          setSelectedEpisodes([episode.id]);
                        }}
                      >
                        Obavijesti me
                      </button>
                    </div>
                  </div>
                </div>
              ))}
                </div>
              </div>

              {/* Mobile: Vertical Stack */}
              <div className="lg:hidden space-y-6">
            {upcomingEpisodes.map((episode) => (
              <div
                key={episode.id}
                className="relative bg-charcoal/40 backdrop-blur-sm border border-slate-dark/30 rounded-3xl overflow-hidden hover:scale-[1.01] transition-all duration-500 group cursor-pointer"
                onClick={() => setSelectedEpisode(episode)}
              >
                {/* Background Image */}
                <div 
                  className="absolute inset-0 bg-cover bg-center opacity-20 group-hover:opacity-40 transition-opacity duration-500"
                  style={{ backgroundImage: `url(${episode.background})` }}
                />
                
                {/* Dark Overlay */}
                <div className="absolute inset-0 bg-gradient-to-t from-midnight via-midnight/90 to-midnight/60" />

                {/* Content */}
                <div className="relative p-6">
                  <div className="flex items-center justify-center mb-4">
                    <span className="bg-electric-yellow text-midnight px-3 py-1 rounded-full text-xs font-bold uppercase tracking-wide">
                      COMING SOON
                    </span>
                  </div>

                  <h3 className="font-clash text-xl font-bold leading-tight mb-3 group-hover:text-electric-yellow transition-colors duration-500">
                    {episode.title}
                  </h3>

                  <p className="text-soft-gray mb-2 text-sm uppercase tracking-wide">
                    {episode.isAnonymous ? episode.guestHint : `Guest: ${episode.guestName || episode.guestHint}`}
                  </p>

                  <blockquote className="text-electric-yellow italic mb-4">
                    "{episode.quote}"
                  </blockquote>

                  {/* Countdown & CTA */}
                  <div className="flex items-center justify-between">
                    <div className="bg-midnight/60 rounded-lg px-3 py-2">
                      <div className="text-electric-yellow font-bold text-sm">
                        {countdowns[episode.id]?.days || 0}d {countdowns[episode.id]?.hours || 0}h {countdowns[episode.id]?.minutes || 0}m
                      </div>
                    </div>
                    <button 
                      className="bg-electric-yellow text-midnight px-4 py-2 rounded-lg font-bold text-sm hover:bg-electric-yellow/90 transition-all duration-300"
                      onClick={(e) => {
                        e.stopPropagation();
                        setShowNotifyModal(true);
                        setSelectedEpisodes([episode.id]);
                      }}
                    >
                      Obavijesti me
                    </button>
                  </div>
                </div>
                </div>
              ))}
              </div>
            </>
          )}
        </div>
      </div>

      {/* CTA Section */}
      <div className="px-6 lg:px-12 pb-20">
        <div className="max-w-4xl mx-auto text-center">
          <div className="bg-gradient-to-r from-deep-teal/20 to-electric-yellow/10 backdrop-blur-sm border border-electric-yellow/20 rounded-3xl p-12">
            <h2 className="font-clash text-4xl lg:text-5xl font-bold mb-6">
              Nikad ne propustite novo izdanje
            </h2>
            <p className="text-xl text-soft-gray mb-8">
              Primajte obavještenja o predstojećim epizodama, ekskluzivnom sadržaju iza kulisa, 
              i ranom pristupu našim najiščekivanijim razgovorima.
            </p>
            
            <button 
              className="bg-electric-yellow text-midnight px-8 py-4 rounded-xl font-bold text-lg hover:bg-electric-yellow/90 hover:scale-105 transition-all duration-300"
              onClick={() => {
                setShowNotifyModal(true);
                setSelectedEpisodes(upcomingEpisodes.map(ep => ep.id));
              }}
            >
              Primaj obavještenja za sve epizode
            </button>
          </div>
        </div>
      </div>

      {/* Obavijesti me Modal */}
      {showNotifyModal && (
        <div className="fixed inset-0 bg-midnight/95 backdrop-blur-sm z-50 flex items-center justify-center p-6">
          <div className="bg-charcoal/90 backdrop-blur-md border border-slate-dark/50 rounded-3xl max-w-md w-full p-8">
            <button
              onClick={() => setShowNotifyModal(false)}
              className="absolute top-6 right-6 text-soft-gray hover:text-electric-yellow transition-colors duration-300"
            >
              <CloseIcon />
            </button>

            {!isSubmitted ? (
              <>
                <h3 className="font-clash text-3xl font-bold mb-2 text-electric-yellow">
                  Primaj obavještenja
                </h3>
                <p className="text-soft-gray mb-6">
                  Budite prvi da saznate kada ove epizode izađu. Bez spama, samo dobre stvari.
                </p>

                <form onSubmit={handleNotifySubmit} className="space-y-4">
                  <div>
                    <input
                      type="email"
                      placeholder="Vaša email adresa"
                      value={email}
                      onChange={(e) => setEmail(e.target.value)}
                      required
                      className="w-full bg-midnight/80 border border-slate-dark rounded-xl px-4 py-3 text-warm-white placeholder-soft-gray focus:outline-none focus:ring-2 focus:ring-electric-yellow/50 focus:border-electric-yellow/50 transition-all duration-300"
                    />
                  </div>
                  
                  <div>
                    <input
                      type="text"
                      placeholder="Vaše ime"
                      value={name}
                      onChange={(e) => setName(e.target.value)}
                      required
                      className="w-full bg-midnight/80 border border-slate-dark rounded-xl px-4 py-3 text-warm-white placeholder-soft-gray focus:outline-none focus:ring-2 focus:ring-electric-yellow/50 focus:border-electric-yellow/50 transition-all duration-300"
                    />
                  </div>

                  <div className="bg-midnight/60 rounded-xl p-4">
                    <p className="text-sm text-soft-gray mb-2">Bićete obavješteni o:</p>
                    <p className="text-electric-yellow text-sm">
                      {selectedEpisodes.length === upcomingEpisodes.length 
                        ? 'Svim nadolazećim epizodama' 
                        : `${selectedEpisodes.length} odabran${selectedEpisodes.length > 1 ? 'ih' : 'oj'} epizod${selectedEpisodes.length > 1 ? 'a' : 'i'}`}
                    </p>
                  </div>

                  <button
                    type="submit"
                    className="w-full bg-electric-yellow text-midnight px-6 py-3 rounded-xl font-bold hover:bg-electric-yellow/90 transition-all duration-300"
                  >
                    Obavijesti me
                  </button>
                </form>
              </>
            ) : (
              <div className="text-center py-8">
                <div className="text-6xl mb-4">🎉</div>
                <h3 className="font-clash text-2xl font-bold mb-2 text-electric-yellow">
                  Uključeni ste!
                </h3>
                <p className="text-soft-gray">
                  Sada ste na unutrašnjoj listi. Javićemo vam čim ove epizode budu dostupne.
                </p>
              </div>
            )}
          </div>
        </div>
      )}

      {/* Custom scrollbar styles */}
      <style jsx>{`
        .scrollbar-hide {
          -ms-overflow-style: none;  /* Internet Explorer 10+ */
          scrollbar-width: none;  /* Firefox */
        }
        .scrollbar-hide::-webkit-scrollbar { 
          display: none;  /* Safari and Chrome */
        }
      `}</style>
    </div>
  );
};

export default UpcomingEpisodes;